import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Layers,
  Loader2,
  GitMerge,
  Scissors,
  Plus,
  CheckCircle,
  EyeOff,
  Sparkles,
  Trash2,
  Scan,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { SeverityChip } from './evidence';
import { API } from '../lib/api';
import { isRoomPlanAvailable, scanRoomWithLidar } from '../utils/roomPlanCapture';

// Flattened 2D room: surfaces an inspector should cover. No 3D viewer —
// coverage states over a simple unfolded-box layout.
const SURFACES = [
  { id: 'ceiling', label: 'Ceiling', row: 1, col: 2 },
  { id: 'left_wall', label: 'Left wall', row: 2, col: 1 },
  { id: 'floor', label: 'Floor', row: 2, col: 2 },
  { id: 'right_wall', label: 'Right wall', row: 2, col: 3 },
  { id: 'back_wall', label: 'Back wall', row: 3, col: 2 },
  { id: 'front_wall', label: 'Front wall', row: 4, col: 2 },
];

const EXTRA_VIEWS = [
  { id: 'overview', label: 'Overview' },
  { id: 'detail', label: 'Detail' },
];

const MANUAL_FINDING_TYPES = [
  { value: 'odor', label: 'Odor (smoke, pets, mold)' },
  { value: 'moisture', label: 'Moisture / water indicator' },
  { value: 'soft_floor', label: 'Soft / spongy floor' },
  { value: 'pest_evidence', label: 'Pest evidence' },
  { value: 'instrument_reading', label: 'Instrument reading' },
  { value: 'hidden_damage', label: 'Hidden damage (behind/under)' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES = ['low', 'medium', 'high', 'critical'];

const PROVENANCE_BADGES = {
  ai: { label: 'AI', className: 'bg-marker-soft text-ink border-line-strong' },
  'manual-visible': { label: 'Manual', className: 'bg-marker-soft text-ink border-line-strong' },
  'manual-not-visible': { label: 'Not visible to camera', className: 'bg-sev-med-soft text-sev-med border-sev-med' },
};

function formatType(type) {
  return String(type || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function RoomEvidencePanel({ inspectionId, session, isCompleted, onSelectView }) {
  const [findings, setFindings] = useState([]);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manual, setManual] = useState({
    finding_type: 'odor',
    severity: 'medium',
    location: '',
    description: '',
    provenance: 'manual-not-visible',
    surface_id: '',
  });
  const [lidarAvailable, setLidarAvailable] = useState(false);
  const [lidarScanning, setLidarScanning] = useState(false);
  const [lidarDims, setLidarDims] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const status = await isRoomPlanAvailable();
      if (!cancelled) setLidarAvailable(!!status.available);
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchFindings = useCallback(async () => {
    if (!inspectionId) return;
    setLoadingFindings(true);
    try {
      const res = await axios.get(`${API}/inspections/${inspectionId}/findings`);
      setFindings(res.data.findings || []);
    } catch {
      // Findings are additive; a fetch failure shouldn't break the page.
    } finally {
      setLoadingFindings(false);
    }
  }, [inspectionId]);

  useEffect(() => { fetchFindings(); }, [fetchFindings]);

  const capturedViews = useMemo(
    () => new Set((session?.observations || []).map((o) => o.view_label)),
    [session]
  );

  const roomFindings = useMemo(
    () => findings.filter((f) => !session || f.room_key === session.room_key),
    [findings, session]
  );
  const duplicates = useMemo(
    () => roomFindings.filter((f) => f.status === 'proposed' && f.observation_count > 1),
    [roomFindings]
  );
  const manualFindings = useMemo(
    () => roomFindings.filter((f) => f.provenance !== 'ai'),
    [roomFindings]
  );

  const runConsolidate = async () => {
    setConsolidating(true);
    try {
      const res = await axios.post(`${API}/inspections/${inspectionId}/findings/consolidate`);
      setFindings(res.data.findings || []);
      const n = res.data.duplicate_candidates || 0;
      if (n > 0) {
        toast.warning(`${n} possible duplicate${n > 1 ? 's' : ''} found — review below before trusting totals`);
      } else {
        toast.success('No duplicate damages detected across photos');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Duplicate scan failed');
    } finally {
      setConsolidating(false);
    }
  };

  const confirmFinding = async (findingId) => {
    setActioningId(findingId);
    try {
      await axios.post(`${API}/inspections/${inspectionId}/findings/${findingId}/confirm`);
      toast.success('Merge confirmed — counted as one damage');
      fetchFindings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    } finally {
      setActioningId(null);
    }
  };

  const splitFinding = async (findingId) => {
    setActioningId(findingId);
    try {
      await axios.post(`${API}/inspections/${inspectionId}/findings/${findingId}/split`);
      toast.success('Split into separate damages');
      fetchFindings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to split');
    } finally {
      setActioningId(null);
    }
  };

  const deleteFinding = async (findingId) => {
    setActioningId(findingId);
    try {
      await axios.delete(`${API}/inspections/${inspectionId}/findings/${findingId}`);
      toast.success('Finding removed');
      fetchFindings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove finding');
    } finally {
      setActioningId(null);
    }
  };

  const runLidarScan = async () => {
    setLidarScanning(true);
    try {
      const geometry = await scanRoomWithLidar();
      setLidarDims(geometry?.dimensions || null);
      toast.success(
        geometry?.dimensions
          ? `Room scanned: ${Number(geometry.dimensions.width_m).toFixed(1)}×${Number(geometry.dimensions.depth_m).toFixed(1)} m`
          : 'LiDAR room scan captured'
      );
    } catch (err) {
      toast.error(err.message || 'LiDAR scan failed');
    } finally {
      setLidarScanning(false);
    }
  };

  const openManualDialog = (surfaceId = '') => {
    setManual({
      finding_type: 'odor',
      severity: 'medium',
      location: '',
      description: '',
      provenance: 'manual-not-visible',
      surface_id: surfaceId,
    });
    setManualOpen(true);
  };

  const submitManual = async () => {
    if (!session?.room_name) { toast.error('Select a room first'); return; }
    if (manual.description.trim().length < 5) {
      toast.error('Describe the finding (at least 5 characters)');
      return;
    }
    setManualSaving(true);
    try {
      await axios.post(`${API}/inspections/${inspectionId}/findings`, {
        room_name: session.room_name,
        finding_type: manual.finding_type,
        severity: manual.severity,
        location: manual.location.trim(),
        description: manual.description.trim(),
        provenance: manual.provenance,
        surface_id: manual.surface_id || null,
      });
      toast.success('Manual finding recorded');
      setManualOpen(false);
      fetchFindings();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save finding');
    } finally {
      setManualSaving(false);
    }
  };

  if (!session) return null;

  const surfaceTile = (surface) => {
    const covered = capturedViews.has(surface.id);
    return (
      <button
        key={surface.id}
        type="button"
        disabled={isCompleted}
        onClick={() => {
          onSelectView?.(surface.id);
          if (!covered) toast.info(`Next photo will be tagged "${surface.label}"`);
        }}
        style={{ gridRow: surface.row, gridColumn: surface.col }}
        className={`rounded-md border px-2 py-2.5 text-center transition-colors disabled:cursor-default ${
          covered
            ? 'border-sev-low bg-sev-low-soft text-sev-low'
            : 'border-dashed border-line-strong bg-sunken text-ink-muted hover:border-ink hover:text-ink'
        }`}
        data-testid={`coverage-tile-${surface.id}`}
      >
        <p className="text-xs font-semibold">{surface.label}</p>
        <p className="text-xs mt-0.5">{covered ? 'Captured' : 'Not captured'}</p>
      </button>
    );
  };

  return (
    <div className="bg-surface border border-line rounded-md p-5" data-testid="room-evidence-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="m-0 inline-flex items-center gap-2 font-display text-[22px] font-bold leading-7">
          <Layers className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" /> Room evidence: {session.room_name}
        </h3>
        <div className="flex gap-2 flex-wrap">
          {!isCompleted && (
            <Button
              onClick={() => openManualDialog('')}
              variant="outline"
              className="h-8 text-xs border-line-strong text-ink"
              data-testid="add-manual-finding-button"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Manual finding
            </Button>
          )}
          {lidarAvailable && !isCompleted && (
            <Button
              onClick={runLidarScan}
              disabled={lidarScanning}
              variant="outline"
              className="h-8 text-xs border-line-strong text-ink"
              data-testid="lidar-scan-button"
            >
              {lidarScanning ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Scan className="w-3.5 h-3.5 mr-1" />}
              Scan with LiDAR
            </Button>
          )}
          <Button
            onClick={runConsolidate}
            disabled={consolidating}
            variant="outline"
            className="h-8 text-xs border-line-strong text-ink hover:bg-marker-soft"
            data-testid="scan-duplicates-button"
          >
            {consolidating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Scan for duplicates
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-4">
        {/* 2D coverage canvas */}
        <div>
          <p className="text-xs font-semibold text-ink-muted mb-2">Coverage — tap a surface to tag your next photo</p>
          <div className="grid grid-cols-3 gap-1.5 max-w-[340px]">
            {SURFACES.map(surfaceTile)}
          </div>
          <div className="flex gap-1.5 mt-2">
            {EXTRA_VIEWS.map((v) => {
              const covered = capturedViews.has(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={isCompleted}
                  onClick={() => onSelectView?.(v.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-default ${
                    covered
                      ? 'border-sev-low bg-sev-low-soft text-sev-low'
                      : 'border-dashed border-line-strong bg-sunken text-ink-muted hover:border-ink hover:text-ink'
                  }`}
                >
                  {v.label} {covered ? '✓' : ''}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-ink-muted mt-2">
            {capturedViews.size >= 5
              ? 'Good coverage — most surfaces documented.'
              : 'Aim for overview + each wall + floor so nothing is missed in a dispute.'}
          </p>
          {lidarDims && (
            <p className="text-xs text-ink-muted mt-1 font-mono" data-testid="lidar-dimensions">
              LiDAR: {Number(lidarDims.width_m || 0).toFixed(1)} × {Number(lidarDims.depth_m || 0).toFixed(1)} × {Number(lidarDims.height_m || 0).toFixed(1)} m
            </p>
          )}
        </div>

        {/* Duplicate review + manual findings */}
        <div className="space-y-3">
          {duplicates.length > 0 && (
            <div className="rounded-md border border-sev-med bg-sev-med-soft p-3" data-testid="duplicate-review">
              <p className="text-xs font-semibold text-sev-med mb-2">
                Possible duplicates — same damage seen in multiple photos?
              </p>
              <div className="space-y-2">
                {duplicates.map((f) => (
                  <div key={f.id} className="rounded-md bg-surface border border-sev-med p-2.5">
                    <p className="text-sm font-semibold text-ink">
                      {formatType(f.finding_type)}
                      <span className="text-xs font-normal text-ink-muted"> · {f.observation_count} observations</span>
                    </p>
                    {f.location && <p className="text-xs text-ink-muted mt-0.5">{f.location}</p>}
                    <div className="flex gap-2 mt-2">
                      <Button
                        onClick={() => confirmFinding(f.id)}
                        disabled={actioningId === f.id}
                        className="h-7 text-xs bg-sev-low hover:bg-sev-low text-surface"
                        data-testid={`confirm-merge-${f.id}`}
                      >
                        <GitMerge className="w-3 h-3 mr-1" /> Same damage
                      </Button>
                      <Button
                        onClick={() => splitFinding(f.id)}
                        disabled={actioningId === f.id}
                        variant="outline"
                        className="h-7 text-xs border-line-strong text-ink"
                        data-testid={`split-finding-${f.id}`}
                      >
                        <Scissors className="w-3 h-3 mr-1" /> Separate damages
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-ink-muted mb-2">
              Findings the camera can&apos;t prove {loadingFindings && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
            </p>
            {!manualFindings.length ? (
              <p className="text-xs text-ink-muted">
                None recorded. Use &quot;Manual finding&quot; for odors, soft floors, moisture readings, or anything hidden from the camera.
              </p>
            ) : (
              <ul className="space-y-2">
                {manualFindings.map((f) => {
                  const badge = PROVENANCE_BADGES[f.provenance] || PROVENANCE_BADGES['manual-visible'];
                  return (
                    <li key={f.id} className="rounded-md border border-line bg-sunken p-2.5" data-testid={`manual-finding-${f.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-ink">{formatType(f.finding_type)}</p>
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1 ${badge.className}`}>
                          {f.provenance === 'manual-not-visible' && <EyeOff className="w-2.5 h-2.5" />}
                          {badge.label}
                        </span>
                        {f.severity && <SeverityChip severity={f.severity} />}
                        {!isCompleted && (
                          <button
                            type="button"
                            onClick={() => deleteFinding(f.id)}
                            disabled={actioningId === f.id}
                            className="ml-auto text-ink-muted hover:text-sev-high"
                            aria-label="Delete finding"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-ink-muted mt-1">{f.description}</p>
                      {(f.location || f.surface_id) && (
                        <p className="text-xs text-ink-muted mt-0.5">
                          {f.surface_id ? `${formatType(f.surface_id)}` : ''}{f.surface_id && f.location ? ' — ' : ''}{f.location}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {roomFindings.some((f) => f.provenance === 'ai' && f.status === 'confirmed') && (
            <p className="text-xs text-sev-low inline-flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {roomFindings.filter((f) => f.provenance === 'ai' && f.status === 'confirmed').length} AI finding(s) reviewed for this room
            </p>
          )}
        </div>
      </div>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="bg-surface border-line sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-ink">Manual finding — {session.room_name}</DialogTitle>
            <DialogDescription className="text-ink-muted">
              Record damage the camera can&apos;t prove: odors, soft spots, moisture, hidden issues.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-ink text-xs">Type</Label>
                <Select value={manual.finding_type} onValueChange={(v) => setManual((m) => ({ ...m, finding_type: v }))}>
                  <SelectTrigger className="bg-sunken border-line h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface">
                    {MANUAL_FINDING_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-ink text-xs">Severity</Label>
                <Select value={manual.severity} onValueChange={(v) => setManual((m) => ({ ...m, severity: v }))}>
                  <SelectTrigger className="bg-sunken border-line h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface">
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-ink text-xs">Surface (optional)</Label>
              <Select
                value={manual.surface_id || '__none__'}
                onValueChange={(v) => setManual((m) => ({ ...m, surface_id: v === '__none__' ? '' : v }))}
              >
                <SelectTrigger className="bg-sunken border-line h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface">
                  <SelectItem value="__none__">Not tied to a surface</SelectItem>
                  {SURFACES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-ink text-xs">Location detail (optional)</Label>
              <Input
                value={manual.location}
                onChange={(e) => setManual((m) => ({ ...m, location: e.target.value }))}
                placeholder="e.g. under the window, behind the fridge"
                className="bg-sunken border-line h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ink text-xs">Description</Label>
              <Textarea
                value={manual.description}
                onChange={(e) => setManual((m) => ({ ...m, description: e.target.value }))}
                placeholder="e.g. Strong pet odor concentrated near the carpet; strongest by the closet."
                className="bg-sunken border-line min-h-[80px] text-sm"
                data-testid="manual-finding-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ink text-xs">Can the camera see it?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setManual((m) => ({ ...m, provenance: 'manual-visible' }))}
                  className={`h-9 text-xs font-semibold rounded-md border ${manual.provenance === 'manual-visible'? 'bg-ink border-ink text-paper': 'bg-surface border-line-strong text-ink-muted'}`}
                >
                  Visible
                </button>
                <button
                  type="button"
                  onClick={() => setManual((m) => ({ ...m, provenance: 'manual-not-visible' }))}
                  className={`h-9 text-xs font-semibold rounded-md border ${manual.provenance === 'manual-not-visible'? 'bg-sev-med border-sev-med text-surface': 'bg-surface border-line-strong text-ink-muted'}`}
                >
                  Not visible
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} className="border-line-strong">Cancel</Button>
            <Button onClick={submitManual} disabled={manualSaving} className="bg-marker hover:bg-marker-hover text-on-marker" data-testid="manual-finding-submit">
              {manualSaving ? 'Saving…' : 'Save finding'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
